import AbstractPlayerEventConsumer from 'components/pages/playback/player-event-consumers/AbstractPlayerEventConsumer';
import { Lightning } from '@lightningjs/sdk';
import { BcPlayerEvents, CustomPlayerEvent } from 'types/player';
import { BrightcoveState } from 'components/pages/playback/player-engines/BrightcoveEngine';
import type { BcPlayer } from 'types/brightcove';
export interface BrightcovePlayerEventConsumerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  brightcoveInstance: BcPlayer | null;
}

export default class BrightcovePlayerEventConsumer extends AbstractPlayerEventConsumer<BrightcovePlayerEventConsumerTemplateSpec> {
  private _brightcoveInstance: BcPlayer | null = null;

  private currentState: BrightcoveState | null = null;
  private previousState: BrightcoveState | null = null;

  override _inactive(): void {
    this.currentState = null;
    this.previousState = null;
  }
  set brightcoveInstance(brightcoveInstance: BcPlayer) {
    this._brightcoveInstance = brightcoveInstance;
    this.addBrightcoveListeners();
    this.addPlayerElementListeners();
  }

  private signalMap = {
    [BcPlayerEvents.PlayerAbort]: '$videoPlayerAbort',
    [BcPlayerEvents.PlayerCanPlay]: '$videoPlayerCanPlay',
    [BcPlayerEvents.PlayerCanPlayThrough]: '$videoPlayerCanPlayThrough',
    // [Events.PlayerWaiting]: '$videoPlayerWaiting', // handled by player state
    [BcPlayerEvents.PlayerDurationChanged]: '$videoPlayerDurationChange',
    [BcPlayerEvents.PlayerEmptied]: '$videoPlayerEmptied',
    // [Player.Event.]: CONSUMER_EVENTS.$videoPlayerEncrypted, // event missing
    [BcPlayerEvents.PlayerEnded]: '$videoPlayerEnded',
    [BcPlayerEvents.PlayerError]: '$videoPlayerError',
    // [Player.Event.]: CONSUMER_EVENTS.$videoPlayerInterruptBegin, // event missing
    // [Player.Event.]: CONSUMER_EVENTS.$videoPlayerInterruptEnd, // event missing
    [BcPlayerEvents.PlayerLoadedData]: '$videoPlayerLoadedData',
    [BcPlayerEvents.PlayerLoadedMetadata]: '$videoPlayerLoadedMetadata',
    [BcPlayerEvents.PlayerLoadStart]: '$videoPlayerLoadStart',
    // [Events.PlayerPlay]: '$videoPlayerPlay', // handled by player state
    // [Events.PlayerPause]: '$videoPlayerPause', // handled by player state
    [BcPlayerEvents.PlayerPlaying]: '$videoPlayerPlaying',
    [BcPlayerEvents.PlayerProgress]: '$videoPlayerProgress',
    [BcPlayerEvents.PlayerRateChanged]: '$videoPlayerRatechange',
    [BcPlayerEvents.PlayerSeeked]: '$videoPlayerSeeked',
    [BcPlayerEvents.PlayerStalled]: '$videoPlayerStalled',
    // [BcPlayerEvents.PlayerTimeChanged]: '$videoPlayerTimeUpdate', // this does not reflect ads, use video-js's timeupdate instead
    [BcPlayerEvents.PlayerVolumeChanged]: '$videoPlayerVolumeChange',
    // [Player.Event.]: CONSUMER_EVENTS.$videoPlayerClear, // event missing
    // [Player.Event.]: CONSUMER_EVENTS.$videoManagerAdBreakStarted,
    // [Player.Event.]: CONSUMER_EVENTS.$videoManagerStarted, // using video-js event instead
    // [Player.Event.]: CONSUMER_EVENTS.$videoManagerComplete, // using video-js event instead
    // [Player.Event.PlayerWaiting]: CONSUMER_EVENTS.$videoManagerAdBreakEnded,
    [BcPlayerEvents.Ads.AdEnd]: '$adBreakEnded',
    // [BcPlayerEvents.Ads.AdSkip]: '$',
    [BcPlayerEvents.Ads.AdStart]: '$adBreakStarted',
    // [BcPlayerEvents.Ads.AdTimeout]: '$',
    // [BcPlayerEvents.Ads.Cancelled]: '$',
    // [BcPlayerEvents.Ads.ContentChanged]: '$',
    // [BcPlayerEvents.Ads.ContentResumed]: '$',
    // [BcPlayerEvents.Ads.ContentUpdate]: '$',
    // [BcPlayerEvents.Ads.NoPostroll]: '$',
    // [BcPlayerEvents.Ads.NoPreroll]: '$',
    // [BcPlayerEvents.Ads.Ready]: '$',
    // [BcPlayerEvents.Ads.ReadyForPostroll]: '$',
    // [BcPlayerEvents.Ads.ReadyForPreroll]: '$',
  } as const;

  private addBrightcoveListeners() {
    if (!this._brightcoveInstance) return;

    Object.keys(this.signalMap).forEach(key => {
      const typedKey = key as keyof typeof this.signalMap;
      const eventFn = this.signalMap[typedKey];
      this._brightcoveInstance!.addEventListener(typedKey, (...args: any) => {
        const rawTime = this._brightcoveInstance!.getCurrentTime();

        if (
          eventFn === '$videoPlayerCanPlay' ||
          eventFn === '$videoPlayerCanPlayThrough'
        ) {
          /* 
            playback will resume if:
            player was previously playing, the player is currently playing, or playback just started
          */
          const willResume =
            this.previousState === BrightcoveState.Playing ||
            this.currentState === BrightcoveState.Playing ||
            this.previousState === null;
          this[eventFn](null, rawTime, willResume);
        } else if (eventFn && eventFn in this) {
          this[eventFn](null, rawTime);
        }
      });
    });

    this._brightcoveInstance!.addEventListener(
      BcPlayerEvents.PlayerStateChanged,
      event => {
        const state = event.playerState as unknown as BrightcoveState;
        const rawTime = this._brightcoveInstance!.getCurrentTime();

        this.previousState = this.currentState;
        this.currentState = state;

        switch (state) {
          // Since PlayerStateChanged only reflects the state of the main playback,
          // these playing and paused events are not emitted in ads.
          // So must be handled by player events instead.

          // case BrightcoveState.Playing:
          //   this.$videoPlayerPlay(null, rawTime);
          //   break;
          // case BrightcoveState.Paused:
          //   this.$videoPlayerPause(null, rawTime);
          //   break;
          case BrightcoveState.Buffering:
            this.$videoPlayerWaiting(null, rawTime);
            break;
          default:
            break;
        }
      },
    );
  }

  // TODO: remove listeners
  private addPlayerElementListeners() {
    const video = this._brightcoveInstance?.getVideoElement();
    if (!video) return;

    video.addEventListener('play', () => {
      const rawTime = this._brightcoveInstance!.getCurrentTime();
      this.$videoPlayerPlay(null, rawTime);
    });
    video.addEventListener('pause', () => {
      const rawTime = this._brightcoveInstance!.getCurrentTime();
      this.$videoPlayerPause(null, rawTime);
    });

    video.addEventListener('timeupdate', (event: any) => {
      // BcPlayerEvents.PlayerTimeChanged only fires during main playback
      // To also account for ad playback, use this listener
      this.$videoPlayerTimeUpdate(event, video.currentTime);
    });

    // https://player.support.brightcove.com/advertising/ad-events-and-ad-objects.html
    // https://videojs.github.io/videojs-contrib-ads/integrator/api.html
    const videoJsPlayer = (document.getElementsByTagName('video-js')[0] as any)
      ?.player;
    if (videoJsPlayer) {
      videoJsPlayer.on('ads-ad-started', (event: any) => {
        // BcPlayerEvents.PlayerCanPlay doesn't emit during ads,
        // we should manually call it here instead, to avoid infinite loading indicator.
        this.$videoPlayerCanPlay(
          null,
          this._brightcoveInstance!.getCurrentTime(),
        );
        this.signal('$videoManagerStarted', event);
      });
      videoJsPlayer.on('ads-ad-ended', (event: any) => {
        this.signal('$videoManagerComplete', event);
      });
    }
  }

  $adBreakStarted() {
    this.signal('$videoManagerAdBreakStarted');
  }

  $adBreakEnded() {
    this.signal('$videoManagerAdBreakEnded');
  }

  $videoPlayerEvent(type: string, event: any, ...args: any) {
    switch (type) {
      case CustomPlayerEvent.SEEKING:
        this.signal('$videoPlayerSeeking', args[0]);
        break;
      case CustomPlayerEvent.SEEK_PAUSE: {
        this.signal('$videoManagerSeekPause', event, ...args);
        break;
      }
      case CustomPlayerEvent.BEFORE_SEEK: {
        this.signal('$videoManagerBeforeSeek', event, ...args);
        break;
      }
      case CustomPlayerEvent.SKIP_AD_POD: // Skipping ad pod that has already been watched
        this.signal('$videoManagerSkipAdPod', type, event, ...args);
        break;
      case CustomPlayerEvent.CLEANUP_AD_POD: // AD_BREAK_END is never called despite finishing ad
        this.signal('$videoManagerCleanupAdPod', type, event, ...args);
        break;
      default:
        this.signal('$videoPlayerEvent', type, event, ...args);
        break;
    }
  }
}
